// services/api.js
// Axios instance מרכזי — כל קריאות ה-API עוברות דרכו
//
// אחריות:
// 1. Base URL מ-env
// 2. Token management ב-memory (לא localStorage!)
// 3. Request interceptor — מוסיף Authorization header
// 4. Response interceptor — error handling אחיד
// 5. Token refresh אוטומטי כל ~25 דקות
// 6. API Log (test mode) — רישום כל קריאה למערך
// 7. CSV download helper — blob handling
// 8. Multipart upload helper

import axios from 'axios';

// =============================================================================
// Base URL
// =============================================================================
const BASE_URL = process.env.REACT_APP_API_URL || '/api.php';
const TEST_MODE = process.env.REACT_APP_TEST_MODE === 'true';

// =============================================================================
// Token management — ב-memory בלבד, לא localStorage!
// =============================================================================
let _token = null;
let _refreshTimer = null;
const TOKEN_REFRESH_INTERVAL = 25 * 60 * 1000; // 25 דקות

// Callbacks שירשמו על ידי AuthContext
let _onTokenExpired = null;     // נקרא כשה-token פג (401)
let _onTokenRefreshed = null;   // נקרא כשה-token התחדש

export function setToken(token) {
  _token = token;
  if (token) {
    _startRefreshTimer();
  } else {
    _stopRefreshTimer();
  }
}

export function getToken() {
  return _token;
}

export function clearToken() {
  _token = null;
  _stopRefreshTimer();
}

export function setAuthCallbacks({ onTokenExpired, onTokenRefreshed }) {
  _onTokenExpired = onTokenExpired || null;
  _onTokenRefreshed = onTokenRefreshed || null;
}

// =============================================================================
// Token refresh timer
// =============================================================================
function _startRefreshTimer() {
  _stopRefreshTimer();
  _refreshTimer = setInterval(async () => {
    if (!_token) {
      _stopRefreshTimer();
      return;
    }
    try {
      const response = await api.post('/auth/refresh');
      if (response.data?.success && response.data?.data?.token) {
        _token = response.data.data.token;
        if (_onTokenRefreshed) {
          _onTokenRefreshed(response.data.data.token);
        }
      }
    } catch (err) {
      // refresh נכשל — token פג
      // אם 401 → ה-response interceptor יטפל
      // שגיאות אחרות → ננסה שוב ב-interval הבא
      if (err.response?.status === 401) {
        clearToken();
        if (_onTokenExpired) {
          _onTokenExpired();
        }
      }
    }
  }, TOKEN_REFRESH_INTERVAL);
}

function _stopRefreshTimer() {
  if (_refreshTimer) {
    clearInterval(_refreshTimer);
    _refreshTimer = null;
  }
}

// =============================================================================
// API Log — רישום כל קריאות API (test mode בלבד)
// =============================================================================
const apiLog = [];
let _apiLogIdCounter = 0;

export function getApiLog() {
  return [...apiLog];
}

export function getLastApiCall() {
  return apiLog.length > 0 ? { ...apiLog[apiLog.length - 1] } : null;
}

export function clearApiLog() {
  apiLog.length = 0;
}

export function waitForApiCall(urlPattern, timeout = 5000) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const startLength = apiLog.length;

    const check = () => {
      // חפש בקריאות חדשות שנוספו מאז תחילת ההמתנה
      for (let i = startLength; i < apiLog.length; i++) {
        const entry = apiLog[i];
        if (entry.url && entry.url.includes(urlPattern) && entry.status !== undefined) {
          resolve({ ...entry });
          return;
        }
      }
      if (Date.now() - startTime > timeout) {
        reject(new Error(`waitForApiCall: timeout waiting for ${urlPattern}`));
        return;
      }
      setTimeout(check, 100);
    };
    check();
  });
}

// =============================================================================
// Axios Instance
// =============================================================================
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// =============================================================================
// Public endpoints — לא דורשים token
// =============================================================================
const PUBLIC_ENDPOINTS = [
  { method: 'post', path: '/auth/login' },
  { method: 'get', path: '/company' },
];

function _isPublicEndpoint(method, url) {
  const normalizedUrl = url.replace(BASE_URL, '');
  return PUBLIC_ENDPOINTS.some(
    (ep) => ep.method === method.toLowerCase() && normalizedUrl.startsWith(ep.path)
  );
}

// =============================================================================
// Request Interceptor
// =============================================================================
api.interceptors.request.use(
  (config) => {
    // הוספת token ל-header (חוץ מ-public endpoints)
    if (_token && !_isPublicEndpoint(config.method, config.url)) {
      config.headers.Authorization = `Bearer ${_token}`;
    }

    // רישום ב-API Log (test mode)
    if (TEST_MODE) {
      // Build full URL including query params for test visibility
      let fullUrl = config.url;
      if (config.params && Object.keys(config.params).length > 0) {
        const qs = Object.entries(config.params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join('&');
        if (qs) fullUrl += `?${qs}`;
      }

      const logEntry = {
        id: ++_apiLogIdCounter,
        timestamp: new Date().toISOString(),
        method: config.method?.toUpperCase(),
        url: fullUrl,
        path: config.url,
        params: config.params || null,
        body: config.data || null,
        // status, response, duration — יתמלאו ב-response interceptor
        status: undefined,
        response: undefined,
        duration: undefined,
      };
      apiLog.push(logEntry);
      // שמור את ה-ID ב-config לשימוש ב-response interceptor (immune to clearApiLog)
      config._apiLogId = logEntry.id;
      config._apiLogStart = performance.now();
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// =============================================================================
// Response Interceptor
// =============================================================================
api.interceptors.response.use(
  // ======= הצלחה =======
  (response) => {
    // עדכון API Log
    if (TEST_MODE && response.config._apiLogId !== undefined) {
      const entry = apiLog.find(e => e.id === response.config._apiLogId);
      if (entry) {
        entry.status = response.status;
        entry.response = response.data;
        entry.duration = Math.round(performance.now() - response.config._apiLogStart);
      }
    }

    return response;
  },

  // ======= שגיאה =======
  (error) => {
    const response = error.response;
    const config = error.config;

    // עדכון API Log
    if (TEST_MODE && config?._apiLogId !== undefined) {
      const entry = apiLog.find(e => e.id === config._apiLogId);
      if (entry) {
        entry.status = response?.status || 0;
        entry.response = response?.data || { error: error.message };
        entry.duration = Math.round(performance.now() - config._apiLogStart);
      }
    }

    // טיפול ב-401 — token פג
    if (response?.status === 401) {
      // לא מנסים redirect אם זה login או refresh שנכשל
      const url = config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/refresh')) {
        clearToken();
        if (_onTokenExpired) {
          _onTokenExpired();
        }
      }
    }

    return Promise.reject(error);
  }
);

// =============================================================================
// CSV Download Helper
// =============================================================================
// כל endpoints של /export/* מחזירים raw CSV (לא JSON!)
// ★ חובה: responseType: 'blob' — אסור לנסות parse כ-JSON
// ★ ה-CSV מתחיל ב-BOM (EF BB BF) לתמיכה בעברית ב-Excel
export async function downloadCSV(endpoint, filename) {
  const response = await api.get(endpoint, {
    responseType: 'blob',
    headers: {
      'Accept': 'text/csv',
    },
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'madad_export.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return response;
}

// =============================================================================
// File Upload Helper
// =============================================================================
// לשימוש עם: POST /company/logo (field: 'logo'), POST /backup/upload (field: 'file')
export async function uploadFile(endpoint, file, fieldName = 'file', extraData = {}) {
  const formData = new FormData();
  formData.append(fieldName, file);

  // הוספת שדות נוספים (אם יש)
  Object.entries(extraData).forEach(([key, value]) => {
    formData.append(key, value);
  });

  const response = await api.post(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 60000, // timeout ארוך יותר להעלאות
  });

  return response;
}

// =============================================================================
// Backup Download Helper
// =============================================================================
// GET /backup/download/{filename} — מחזיר קובץ SQL
export async function downloadBackup(filename) {
  const response = await api.get(`/backup/download/${filename}`, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'application/sql' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return response;
}

// =============================================================================
// Error Message Extractor
// =============================================================================
// מחלץ הודעת שגיאה ברורה מ-response
// מבנה שגיאה סטנדרטי: { success: false, data: { message, details } }
export function extractErrorMessage(error, defaultMessage = 'אירעה שגיאה') {
  if (!error) return defaultMessage;

  // Axios error עם response מהשרת
  if (error.response?.data) {
    const serverData = error.response.data;

    // מבנה סטנדרטי: { success, data: { message, details } }
    if (serverData.data?.message) {
      return serverData.data.message;
    }
    // Fallback: message ברמה העליונה
    if (serverData.message) {
      return serverData.message;
    }
  }

  // Network error (אין response)
  if (error.code === 'ECONNABORTED') {
    return 'הבקשה נמשכה יותר מדי זמן. נסה שוב.';
  }
  if (!error.response) {
    return 'אין תקשורת עם השרת. בדוק את החיבור לרשת.';
  }

  // HTTP status fallbacks
  const status = error.response?.status;
  switch (status) {
    case 400: return _extractServerMessage(error) || 'שגיאת קלט';
    case 401: return 'הסשן פג תוקף. יש להתחבר מחדש.';
    case 403: return 'אין לך הרשאה לבצע פעולה זו.';
    case 404: return 'הפריט המבוקש לא נמצא.';
    case 422: return _extractServerMessage(error) || 'שגיאות ולידציה — בדוק את השדות.';
    case 429: return 'יותר מדי בקשות. נסה שוב בעוד מספר דקות.';
    case 500: return 'שגיאת שרת פנימית. נסה שוב מאוחר יותר.';
    default: return defaultMessage;
  }
}

function _extractServerMessage(error) {
  return error.response?.data?.data?.message || error.response?.data?.message || null;
}

// =============================================================================
// Validation Errors Extractor
// =============================================================================
// מחלץ רשימת שדות שגויים מ-422 response
// מבנה: { data: { details: { validation_errors: ["field1", "field2"] } } }
export function extractValidationErrors(error) {
  if (error.response?.status !== 422) return [];
  const details = error.response?.data?.data?.details;
  if (details?.validation_errors && Array.isArray(details.validation_errors)) {
    return details.validation_errors;
  }
  return [];
}

// =============================================================================
// 2FA Detection Helper
// =============================================================================
// בדיקה אם response של 403 הוא בעצם דרישה ל-2FA
// ★ שים לב: requires_2fa ב-response.data.data.details.requires_2fa
// (כי response wrapper הוא { success, data: { message, details: { requires_2fa } } })
// ★ זה לא response.data.requires_2fa !!!
export function is2FARequired(error) {
  if (error.response?.status !== 403) return false;
  const data = error.response?.data?.data;
  return data?.details?.requires_2fa === true;
}

// מחלץ הודעת 2FA המפורטת
export function get2FAMessage(error) {
  const data = error.response?.data?.data;
  // data.details.message הוא ההודעה המפורטת
  // data.message הוא ההודעה הכללית
  return data?.details?.message || data?.message || 'נדרש אימות דו-שלבי';
}

// =============================================================================
// Export
// =============================================================================
export default api;
